import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, Clock, Mail, AlertTriangle, ShieldAlert, History,
} from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-50 text-slate-600 border-slate-200",
};

function hoursSince(iso) {
  if (!iso) return 0;
  return Math.max(0, (Date.now() - new Date(iso).getTime()) / 3.6e6);
}
function urgencyLabel(iso) {
  const h = hoursSince(iso);
  if (h < 18) return null;
  if (h < 24) return { tone: "amber", label: `Auto-escalating in ${Math.round(24 - h)}h` };
  if (h < 48) return { tone: "red", label: `Overdue · ${Math.round(h)}h since applied` };
  return { tone: "red", label: `Overdue · ${Math.floor(h / 24)}d ${Math.round(h % 24)}h` };
}

export default function LeaveApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [dialog, setDialog] = useState(null); // { action, row }
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // HR Override
  const [overrideDialog, setOverrideDialog] = useState(null); // { row, action }
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrides, setOverrides] = useState([]);
  // Detect HR/Super-admin role from session
  const [role, setRole] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leaves");
      setRows(data || []);
      // Load role + override history if HR/super
      try {
        const me = await api.get("/auth/me");
        setRole(me.data?.role || me.data?.user?.role);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      const { data } = await api.get("/leaves/overrides");
      setOverrides(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (tab === "overrides") loadOverrides(); }, [tab]);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [tab, rows]);

  const counts = useMemo(
    () => rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {}),
    [rows]
  );

  const decide = async () => {
    if (!dialog) return;
    setSubmitting(true);
    try {
      await api.post(`/leaves/${dialog.row.id}/${dialog.action}`, { remarks });
      toast.success(`Leave ${dialog.action}d`);
      setDialog(null);
      setRemarks("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const submitOverride = async () => {
    if (!overrideDialog) return;
    if (overrideReason.trim().length < 10) {
      toast.error("Override reason must be at least 10 characters");
      return;
    }
    setOverrideSubmitting(true);
    try {
      await api.post(`/leaves/${overrideDialog.row.id}/override`, {
        action: overrideDialog.action,
        reason: overrideReason.trim(),
      });
      toast.success(`Override applied — leave ${overrideDialog.action}d`);
      setOverrideDialog(null);
      setOverrideReason("");
      refresh();
      if (tab === "overrides") loadOverrides();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setOverrideSubmitting(false);
    }
  };

  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canOverride = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(role);

  return (
    <div className="space-y-5" data-testid="leave-approvals">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 1C</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Leave Approvals
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Approve or reject pending leave requests. Senior Management is cc'd on all decisions.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 p-1 rounded-md">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending {counts.pending ? <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{counts.pending}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved {counts.approved ? `(${counts.approved})` : ""}</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected {counts.rejected ? `(${counts.rejected})` : ""}</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All ({rows.length})</TabsTrigger>
          {canOverride && (
            <TabsTrigger value="overrides" data-testid="tab-overrides">
              <ShieldAlert className="h-3 w-3 mr-1" /> Override History
            </TabsTrigger>
          )}
        </TabsList>

        {tab === "overrides" ? (
          <TabsContent value="overrides" className="mt-4" data-testid="overrides-panel">
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              {overrides.length === 0 ? (
                <div className="text-sm text-slate-500 py-8 text-center" data-testid="overrides-empty">
                  <History className="h-5 w-5 inline-block mr-2 text-slate-400" />
                  No HR overrides recorded yet.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="overrides-table">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Type / Dates</th>
                      <th className="text-left px-3 py-2">Original → New</th>
                      <th className="text-left px-3 py-2">Reason</th>
                      <th className="text-left px-3 py-2">By</th>
                      <th className="text-right px-3 py-2">Ledger Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((o) => (
                      <tr key={o.id} className="border-t border-slate-100" data-testid={`override-row-${o.id}`}>
                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(o.override_at)}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{o.employee_name}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="font-semibold">{o.leave_type}</div>
                          <div className="text-slate-500">{o.from_date} → {o.to_date} ({o.days}d)</div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded border ${STATUS_BADGE[o.original_status] || ""}`}>
                            {o.original_status}
                          </span>
                          <span className="mx-1 text-slate-400">→</span>
                          <span className={`px-1.5 py-0.5 rounded border ${STATUS_BADGE[o.new_status] || ""}`}>
                            {o.new_status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 max-w-[320px] whitespace-normal break-words italic align-top" title={o.override_reason}>
                          &ldquo;{o.override_reason}&rdquo;
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="font-medium">{o.override_by_name}</div>
                          <div className="text-[10px] text-slate-500">{o.override_by_role}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          {o.ledger_delta_days != null ? (
                            <span className={o.ledger_delta_days > 0 ? "text-red-600" : "text-emerald-600"}>
                              {o.ledger_delta_days > 0 ? "−" : "+"}{Math.abs(o.ledger_delta_days)}d
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        ) : (
        <TabsContent value={tab} className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            {loading ? (
              <div className="text-sm text-slate-500 py-6 text-center">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                <Clock className="h-5 w-5 inline-block mr-2 text-slate-400" />
                No {tab} leave requests
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="approvals-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Applied</th>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">From → To</th>
                    <th className="text-right px-3 py-2">Days</th>
                    <th className="text-left px-3 py-2">Reason</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`approval-row-${r.id}`}>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        <div>{formatDate(r.created_at)}</div>
                        {r.status === "pending" && (() => {
                          const u = urgencyLabel(r.created_at);
                          if (!u) return null;
                          return (
                            <div className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                              u.tone === "red"
                                ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            }`} data-testid={`urgency-${r.id}`}>
                              <AlertTriangle className="h-3 w-3" /> {u.label}
                            </div>
                          );
                        })()}
                        {r.is_emergency && (
                          <div className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-red-50 text-red-700 border-red-200"
                               data-testid="emergency-pill">
                            🚨 Emergency
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.employee_name}</div>
                        <div className="text-xs text-slate-500">{r.punch_id}</div>
                      </td>
                      <td className="px-3 py-2 font-semibold">
                        {r.leave_type}
                        {(r.leave_type === "SHORT_EARLY" || r.leave_type === "SHORT_LATE") && r.short_leave_time && (
                          <div className="text-[10px] text-slate-500 font-normal" data-testid="short-leave-info">
                            {r.short_leave_time} · {r.short_leave_minutes || 60} min
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {formatDate(r.from_date)}{r.from_date === r.to_date ? "" : ` → ${formatDate(r.to_date)}`}
                      </td>
                      <td className="px-3 py-2 text-right">{r.days}{r.half_day ? " (½)" : ""}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[320px] whitespace-normal break-words align-top" title={r.reason}>
                        {r.reason || "—"}
                        {r.leave_type === "OD" && r.customer_location && (
                          <div className="text-[10px] text-slate-500 italic mt-0.5" data-testid="customer-location">📍 {r.customer_location}</div>
                        )}
                        {r.medical_certificate_url && (
                          <a href={r.medical_certificate_url} target="_blank" rel="noreferrer"
                             className="text-[10px] underline brand-text mt-0.5 block"
                             data-testid="med-cert-link">
                            📎 Medical certificate ↗
                          </a>
                        )}
                        {r.voice_note_url && (
                          <div className="mt-1" data-testid="voice-note-player">
                            <audio src={r.voice_note_url} controls className="h-6 w-full" preload="none">
                              <track kind="captions" />
                            </audio>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium border px-2 py-0.5 rounded ${STATUS_BADGE[r.status] || ""}`}>
                          {r.status === "approved" && <CheckCircle2 className="h-3 w-3" />}
                          {r.status === "rejected" && <XCircle className="h-3 w-3" />}
                          {r.status === "pending" && <Clock className="h-3 w-3" />}
                          {r.status}
                        </span>
                        {r.is_lwp && r.status === "approved" && (
                          <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> LWP
                          </div>
                        )}
                        {r.is_overridden && (
                          <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"
                               data-testid={`overridden-pill-${r.id}`}>
                            <ShieldAlert className="h-3 w-3" /> HR Override
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.status === "pending" ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setDialog({ action: "approve", row: r }); setRemarks(""); }}
                              className="px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded border border-emerald-200"
                              data-testid={`approval-approve-${r.id}`}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => { setDialog({ action: "reject", row: r }); setRemarks(""); }}
                              className="px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 rounded border border-red-200"
                              data-testid={`approval-reject-${r.id}`}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 space-y-1">
                            <div>
                              {r.decision_by_name || "—"}
                              {r.decision_remarks && (
                                <div className="italic text-[10px] mt-0.5 max-w-[220px] whitespace-normal break-words" title={r.decision_remarks}>
                                  &ldquo;{r.decision_remarks}&rdquo;
                                </div>
                              )}
                            </div>
                            {canOverride && r.status !== "cancelled" && (
                              <button
                                onClick={() => {
                                  // Smart default: if rejected → suggest approve override; if approved → reject
                                  const suggested = r.status === "rejected" ? "approve" : "reject";
                                  setOverrideDialog({ row: r, action: suggested });
                                  setOverrideReason("");
                                }}
                                className="px-2 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded border border-amber-200 inline-flex items-center gap-1"
                                data-testid={`override-btn-${r.id}`}
                              >
                                <ShieldAlert className="h-3 w-3" /> HR Override
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="decide-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {dialog.action === "approve" ? "Approve" : "Reject"} Leave Request
                </DialogTitle>
                <DialogDescription>
                  <span className="block">
                    <b>{dialog.row.employee_name}</b> ({dialog.row.punch_id}) — {dialog.row.leave_type}
                  </span>
                  <span className="block">
                    {formatDate(dialog.row.from_date)}
                    {dialog.row.from_date === dialog.row.to_date ? "" : ` → ${formatDate(dialog.row.to_date)}`}
                    {" · "}{dialog.row.days}{dialog.row.half_day ? " (½)" : ""} day(s)
                  </span>
                  {dialog.row.reason && (
                    <span className="block mt-1 italic text-slate-500">Reason: {dialog.row.reason}</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder={dialog.action === "approve" ? "Optional remarks" : "Reason for rejection"}
                rows={3}
                data-testid="decide-remarks-input"
              />
              <p className="text-[11px] text-slate-500 flex items-center gap-1">
                <Mail className="h-3 w-3" /> Employee + Senior Management will be emailed.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} disabled={submitting}>
                  Close
                </Button>
                <Button
                  onClick={decide}
                  disabled={submitting}
                  className={dialog.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
                  data-testid={`decide-confirm-${dialog.action}`}
                >
                  {submitting ? "Submitting..." : (dialog.action === "approve" ? "Approve" : "Reject")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* HR Override dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={(o) => !o && !overrideSubmitting && setOverrideDialog(null)}>
        <DialogContent data-testid="override-dialog">
          {overrideDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-600" /> HR Override
                </DialogTitle>
                <DialogDescription>
                  This will <b className={overrideDialog.action === "approve" ? "text-emerald-700" : "text-red-700"}>
                    {overrideDialog.action.toUpperCase()}
                  </b>{" "}
                  <span className="font-medium text-slate-900">
                    {overrideDialog.row.employee_name}&apos;s
                  </span>{" "}
                  {overrideDialog.row.leave_type} request for{" "}
                  <span className="font-medium">{overrideDialog.row.days} day(s)</span> ({overrideDialog.row.from_date} → {overrideDialog.row.to_date}),
                  bypassing all policy rules. Current status: <b>{overrideDialog.row.status.toUpperCase()}</b>.
                  {overrideDialog.row.decision_remarks && (
                    <span className="block mt-2 text-[11px] italic text-slate-500">
                      Prior decision: &ldquo;{overrideDialog.row.decision_remarks}&rdquo;
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">
                    Action
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setOverrideDialog({ ...overrideDialog, action: "approve" })}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded border ${
                        overrideDialog.action === "approve"
                          ? "bg-emerald-50 border-emerald-300 text-emerald-700 ring-2 ring-emerald-200"
                          : "bg-white border-slate-200 text-slate-600"
                      }`}
                      data-testid="override-action-approve"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 inline mr-1.5" /> Force Approve
                    </button>
                    <button
                      onClick={() => setOverrideDialog({ ...overrideDialog, action: "reject" })}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded border ${
                        overrideDialog.action === "reject"
                          ? "bg-red-50 border-red-300 text-red-700 ring-2 ring-red-200"
                          : "bg-white border-slate-200 text-slate-600"
                      }`}
                      data-testid="override-action-reject"
                    >
                      <XCircle className="h-3.5 w-3.5 inline mr-1.5" /> Force Reject
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">
                    Override Reason <span className="text-red-500">*</span> <span className="text-slate-400 normal-case">(min 10 chars · audit-logged)</span>
                  </div>
                  <Textarea
                    rows={3}
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="E.g. 'Employee had hospital emergency, missed 48h CL window. Verified with medical certificate.'"
                    data-testid="override-reason-input"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    Employee + Manager + HOD + Senior Mgmt will all receive an email with this reason.
                    {overrideDialog.row.leave_type && ["EL","CL","PL","SL"].includes(overrideDialog.row.leave_type) && (
                      <span className="block mt-1 text-amber-700">
                        ⚠️ This will {overrideDialog.action === "approve"
                          ? `DEDUCT ${overrideDialog.row.days} day(s) from ${overrideDialog.row.leave_type} balance`
                          : (overrideDialog.row.status === "approved"
                              ? `REFUND ${overrideDialog.row.days} day(s) back to ${overrideDialog.row.leave_type} balance`
                              : `not affect ${overrideDialog.row.leave_type} balance`)}.
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOverrideDialog(null)} disabled={overrideSubmitting}>Cancel</Button>
                <Button
                  onClick={submitOverride}
                  disabled={overrideSubmitting || overrideReason.trim().length < 10}
                  className={overrideDialog.action === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"}
                  data-testid="override-confirm-btn"
                >
                  <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                  {overrideSubmitting ? "Applying..." : `Confirm Override (${overrideDialog.action})`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
